/* eslint-disable no-param-reassign */
/* eslint-disable no-throw-literal */
import _ from 'lodash'
import URI from 'urijs'
import moment from 'moment'
import localStorage from 'store'
import { useTranslation } from 'react-i18next'
// import { navigate } from 'gatsby'
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import {
  getCommerceChannel,
  // useAuth,
  useCart,
} from '../../vendor/react-omnitech-api'
import { useCommerceChannel } from '../use-commerce-channel'
import useOrderMethod from '../use-order-method'
import usePriceTemplate from '../use-price-template'
import { useLink } from '../use-link'
import useSku from '../use-sku'
import env from '../../config/env'
import parseStockLevel from '../../helpers/parse-stock-level'
import parseApiCount from '../../helpers/parse-api-count'
import CartContext from './cart-context'

/**
 * CartProvider
 * Contain most logic handing navigate
 */
export default function CartProvider({ value: val, children }) {
  const location = _.get(val, 'location', {})
  // const { auth } = useAuth()
  const { t } = useTranslation()
  const {
    validateSku,
  } = useSku()
  const {
    commerceChannels,
  } = useCommerceChannel()
  const { getPriceTemplate } = usePriceTemplate()
  const {
    availableOrderMethod,
    deliveryAddress,
    getOrderMethodUpdateCartAction,
    orderMethod,
    store,
  } = useOrderMethod()
  const {
    cartIds,
    checkout,
    createCart,
    createIsolateCart,
    fetchCart,
    fetchIsolateCart,
    reset,
    updateCart,
    updateIsolateCart,
  } = useCart()
  const { navigate } = useLink()

  const [carts, setCarts] = useState([])
  const [dineInStagingCart, setDineInStagingCart] = useState({
    actions: [],
    totalItems: 0,
    cartId: null,
  })
  const [refreshCartsInProgress, setRefreshCartsInProgress] = useState(false)
  const [initRefreshCartsReady, setInitRefreshCartsReady] = useState(false)

  const commerceChannelCodes = useMemo(() => (
    _.map(commerceChannels, 'code')
  ), [commerceChannels])

  const commerceChannelCodesForDineIn = useMemo(() => (
    _.map(
      _.filter(availableOrderMethod, { commerceType: 'dineIn' }),
      'commerceChannel',
    )
  ), [availableOrderMethod])
  const dineInCartId = useMemo(() => (
    _.get(cartIds, commerceChannelCodesForDineIn)
  ), [commerceChannelCodesForDineIn, cartIds])

  const cc = useMemo(() => (
    _.get(URI.parseQuery(_.get(location, 'search', '')), 'cc')
  ), [location])

  function isCartInvalid(errors) {
    const invalid = /^(404|20302|21014|21015)$/.test(_.get(errors, 'generalError.code'))
    return invalid
  }

  function isCartCompleted(errors) {
    const invalid = /^(404|21014|21015)$/.test(_.get(errors, 'generalError.code'))
    return invalid
  }

  function isCartFreezed(errors) {
    const invalid = /^(20302)$/.test(_.get(errors, 'generalError.code'))
    return invalid
  }

  const refreshCarts = (params = {}) => {
    if (refreshCartsInProgress) return
    setRefreshCartsInProgress(true)
    const availableCommerceChannelCodes = _.uniq([
      ...commerceChannelCodes,
      ..._.keys(
        _.omitBy(cartIds, _.isEmpty),
      ),
    ])
    const promises = _.map(
      availableCommerceChannelCodes,
      (commerceChannel) => (
        findOrCreateCart({
          cartId: _.get(cartIds, commerceChannel),
          commerceChannel,
          ...params,
        })
      ),
    )
    // const fulfilledCommerceChannelCodes = []
    return Promise.allSettled(promises)
      .then((values) => {
        setCarts(
          _.map(_.filter(values, { status: 'fulfilled' }), ({ value }) => (
            _.get(value, 'cart')
          )),
        )
      })
      .finally(() => {
        setRefreshCartsInProgress(false)
        setInitRefreshCartsReady(true)
      })
  }

  useEffect(() => {
    // Refresh carts every time the browser being inactive for 60s
    let browserInactiveTime = null
    const checkBowserFocusInterval = setInterval(
      () => {
        // setIsBrowserActive(document.hasFocus())
        if (document.hasFocus()) {
          if (
            browserInactiveTime
            && moment().diff(browserInactiveTime, 'seconds') > 60
          ) {
            refreshCarts()
          }
          browserInactiveTime = null
        } else if (!browserInactiveTime) {
          browserInactiveTime = moment().valueOf()
        }
      },
      1000,
    )
    return () => {
      clearInterval(checkBowserFocusInterval)
    }
  }, [cartIds, refreshCarts])

  useEffect(() => {
    // get dineInStagingCart from localStorage
    const dineInStagingCartInLocalStorage = getDineInStagingCart()
    if (_.get(dineInStagingCartInLocalStorage, 'cartId', '--Cart-id-not-found--') !== dineInCartId) {
      clearDineInStagingCart()
    } else {
      setDineInStagingCart(dineInStagingCartInLocalStorage)
    }
  }, [dineInCartId])

  // update dineInStagingCart in localStorage
  useEffect(() => {
    try {
      localStorage.set('dineInStagingCart', dineInStagingCart)
    } catch (error) {
      console.log('//// Error: update dineInStagingCart in localStorage ////', error)
    }
  }, [dineInStagingCart])

  // make sure to refreshCarts once after commerceChannelCodes change
  const commerceChannelCodesDependent = useMemo(() => (
    _.join(
      _.sortBy(
        _.uniq(
          _.compact(commerceChannelCodes),
        ),
      ),
      '|',
    )
  ), [commerceChannelCodes])
  useEffect(() => {
    refreshCarts()
  }, [commerceChannelCodesDependent])

  // // trigger initCart again after user logged out
  // const authToken = useMemo(() => _.get(auth, 'authToken'), [auth])
  // const prevAuthToken = usePrevious(authToken)
  // useEffect(() => {
  //   if (
  //     _.isEmpty(authToken)
  //     && prevAuthToken !== authToken
  //     && !_.isUndefined(prevAuthToken)
  //   ) {
  //     initCart()
  //   }
  // }, [authToken])

  const getParams = (params = {}) => {
    const {
      includes = [],
      // default empty for faster requests. possible values ['price', 'color_option']
      includeGroups = [],
      orderMethodMandatory = true,
      ...otherParams
    } = params;
    const commerceChannel = getCommerceChannel(params)
    const cartId = _.get(otherParams, 'cartId') || _.get(cartIds, commerceChannel)
    const cart = _.find(carts, { id: cartId })
    const selectStoreApproach = _.get(orderMethod, 'selectStoreApproach', {})
    let enabledSelectStoreApproach = _.keys(_.pickBy(selectStoreApproach, _.identity))
    if (_.isEmpty(enabledSelectStoreApproach)) enabledSelectStoreApproach = ['none']
    const storeCode = (
      _.get(store, 'id') !== 'warehouse'
      && orderMethodMandatory
      && _.get(orderMethod, 'commerceChannel') === commerceChannel
      && !_.some(enabledSelectStoreApproach, (code) => _.includes(['none', 'checkout'], code))
      && _.get(store, 'code')
    ) || _.get(cart, 'cartShipments.0.inventoryStore.code')
    return _.assign(
      {},
      {
        includes: _.uniq(_.concat(
          [
            'await_confirm_order',
            'cart_shipments',
            'cart_shipments.inventory_store',
            'cart_line_properties',
            'meta',
            'physical_store_id',
          ],
          _.includes(includeGroups, 'basic')
            ? [
              'available_delivery_types',
              'cart_line_properties.product_addon',
              'cart_line_properties.product',
              'cart_line_properties.sku_id',
              'cart_shipments.courier_service',
              'cart_shipments.delivery_address',
              'cart_shipments.delivery_time_slot',
              'cart_shipments.delivery_type',
              'products.product_addons',
              'skus.meta',
            ]
            : [],
          _.includes(includeGroups, 'preUpdateOrderMethod')
            ? [
              'available_delivery_types',
            ]
            : [],
          _.includes(includeGroups, 'pdp')
            ? [
              'cart_line_properties.color_option',
              'cart_line_properties.sku',
              'cart_line_properties.product',
              'cart_line_properties.product_addon',
              'skus.color_option',
              'skus.product',
              'skus.size_option',
              'skus.stock_level',
            ]
            : [],
          _.includes(includeGroups, 'miniCart')
            ? [
              'cart_line_properties.price_details',
              'cart_line_properties.product_addon',
              'cart_line_properties.product',
              'cart_line_properties.sku',
              'price_details',
              'products.color_option_variant_type',
              'products.size_option_variant_type',
              'skus.active_custom_labels',
              'skus.checkout_settings',
              'skus.color_option',
              'skus.meta',
              'skus.product',
              'skus.size_option',
              'skus.stock_level',
              'stores.today_open_time_slots',
            ]
            : [],
          _.includes(includeGroups, 'dineInMiniCart')
            ? [
              'meta',
              'physical_store_id',
            ]
            : [],
          _.includes(includeGroups, 'dineIn')
            ? [
              'carts.consumer',
              'meta',
              'physical_store_id',
            ]
            : [],
          _.includes(includeGroups, 'cart')
            ? [
              'available_delivery_types',
              'cart_line_properties.color_option',
              'cart_line_properties.price_details',
              'cart_line_properties.product_addon',
              'cart_line_properties.product',
              'cart_line_properties.sku',
              'cart_shipments.pickup_store',
              'price_details',
              'products.color_option_variant_type',
              'products.size_option_variant_type',
              'skus.active_custom_labels',
              'skus.checkout_settings',
              'skus.color_option',
              'skus.meta',
              'skus.product',
              'skus.size_option',
              'skus.stock_level',
              'stores.today_open_time_slots',
            ]
            : [],
          _.includes(includeGroups, 'skuQuota')
            ? [
              'cart_line_properties.sku_quota',
              'skus.sku_quota_profile_id',
              'sku_quotas.sku_quota_master',
              'sku_quotas.store',
              'sku_quotas.salesperson',
            ]
            : [],
          _.includes(includeGroups, 'checkout')
            ? [
              'billing_address',
              'cart_coupon_tokens.coupon_token',
              'cart_coupon_tokens',
              'cart_line_properties.price_details',
              'cart_line_properties.product_addon',
              'cart_line_properties.product',
              'cart_line_properties.color_option',
              'cart_line_properties.size_option',
              'cart_line_properties.sku',
              'cart_shipments.courier_service_id',
              'cart_shipments.courier_service',
              'cart_shipments.delivery_address_id',
              'cart_shipments.delivery_address',
              'cart_shipments.delivery_time_slot_id',
              'cart_shipments.delivery_time_slot',
              'cart_shipments.delivery_type',
              'cart_shipments.pickup_store',
              'categories.department',
              'categories.parent',
              'coupon_tokens.coupon',
              'meta',
              'orders.payment_requests',
              'payment_information',
              'payment_providers.available_payment_provider_tokens',
              'price_details',
              'products.brands',
              'products.categories',
              'products.color_option_variant_type',
              'products.size_option_variant_type',
              'stores.address',
              'stores.meta',
              'stores.today_open_time_slots',
            ]
            : [],
          includes,
        )),
        priceTemplate: _.get(getPriceTemplate(params), 'code'),
        refreshCart: true,
        ...(
          !_.isEmpty(storeCode) ? {
            inventoryStoreCodeEq: storeCode,
            priceStoreCodeEq: storeCode,
          } : {}
        ),
      },
      otherParams,
    )
  }

  const getSkuTotalQuantityInCart = ({
    cart,
    skuId,
    filter = {},
    reject = {},
  }) => {
    let cartLineProperties = _.get(cart, 'cartLineProperties', [])
    if (!_.isEmpty(filter) && _.isObject(filter)) {
      cartLineProperties = _.filter(cartLineProperties, filter)
    }
    if (!_.isEmpty(reject) && _.isObject(reject)) {
      cartLineProperties = _.reject(cartLineProperties, reject)
    }
    const items = _.filter(
      cartLineProperties,
      ({ sku: clpSku, skuId: clpSkuId }) => (_.isEqual(_.get(clpSku, 'id', clpSkuId), skuId)),
    )
    return _.sumBy(items, 'calculationQuantity')
  }

  function getCartLineAvailableQuantity({ cartLine, cart = {} }) {
    const {
      cartMaxNumOfQty = Infinity,
      cartMaxNumOfQtyPerSku = Infinity,
      cartLineProperties = [],
    } = cart
    const {
      groupUuid,
      sku = {},
      skuQuota = {},
      skuId,
      quantity: cartLineQuantity,
    } = cartLine
    let stockLevel = parseStockLevel(sku.stockLevel)
    if (_.has(skuQuota, 'totalQuota')) {
      stockLevel = parseApiCount(_.get(skuQuota, 'totalQuota')) - _.get(skuQuota, 'consumedQuotaCount', 0)
    }
    const mainProperties = _.filter(cartLineProperties, 'isMainProperty')
    const otherCartLines = _.reject(mainProperties, ['groupUuid', groupUuid])
    const otherCartLinesWithSameSku = _.filter(otherCartLines, ({ sku: otherCartLineSku }) => _.get(sku, 'id') === _.get(otherCartLineSku, 'id', '///'))
    const otherCartLinesWithSameSkuQty = _.sumBy(otherCartLinesWithSameSku, 'quantity')
    let availableQtyForCartLine = stockLevel - otherCartLinesWithSameSkuQty
    if (_.has(skuQuota, 'id')) {
      const otherCartLinesWithSameSkuQuota = _.filter(
        otherCartLines,
        ({ skuQuota: otherCartLineSkuQuota }) => (
          _.get(skuQuota, 'id') === _.get(otherCartLineSkuQuota, 'id', '///')
        ),
      )
      const otherCartLinesWithSameSkuQuotaQty = _.sumBy(otherCartLinesWithSameSkuQuota, 'quantity')
      availableQtyForCartLine = _.min([
        stockLevel - otherCartLinesWithSameSkuQuotaQty,
        parseApiCount(_.get(skuQuota, 'quotaPerPerson')),
      ])
    }
    const totalQtyOfOtherCartLines = _.sumBy(otherCartLines, 'quantity')
    const cartMaxAvailableNumOfQty = cartMaxNumOfQty - totalQtyOfOtherCartLines
    const sameSkuInOtherCartLines = _.filter(
      otherCartLines,
      ({ sku: otherCartLineSku, skuId: otherCartLineSkuId }) => (
        _.isEqual(
          _.get(otherCartLineSku, 'id', otherCartLineSkuId),
          _.get(sku, 'id', skuId),
        )
      ),
    )
    const totalQtyOfSameSkuCartLines = _.sumBy(sameSkuInOtherCartLines, 'quantity')
    const cartMaxAvailableNumOfQtyPerSku = cartMaxNumOfQtyPerSku - totalQtyOfSameSkuCartLines

    const allSkus = _.reduce(
      cartLineProperties,
      (result, { sku: addonSku, calculationQuantity }) => {
        const existingSku = _.find(result, { id: _.get(addonSku, 'id') })
        const otherSkus = _.reject(result, { id: _.get(addonSku, 'id') })
        result = [
          ...otherSkus,
          {
            ...addonSku,
            totalQuantity: calculationQuantity + _.get(existingSku, 'totalQuantity', 0),
          },
        ]
        return result
      },
      [],
    )
    const addons = _.filter(cartLineProperties, { groupUuid, isMainProperty: false })
    const payloadStockLevel = _.min(
      _.map(addons, ({ sku: addonSku }) => {
        const addonSkuId = _.get(addonSku, 'id')
        const {
          stockLevel: skuStockLevel,
          totalQuantity,
        } = _.find(allSkus, { id: addonSkuId }) || {}
        const totalQuantityOfAddonSkuInThisCartLine = _.sumBy(
          _.filter(
            addons,
            ({ sku: payloadSku }) => _.get(payloadSku, 'id') === addonSkuId,
          ),
          'quantity',
        )
        return _.floor(
          (parseStockLevel(skuStockLevel) - totalQuantity) / totalQuantityOfAddonSkuInThisCartLine,
        )
      }),
    ) + cartLineQuantity
    return _.min([
      cartMaxAvailableNumOfQty,
      cartMaxAvailableNumOfQtyPerSku,
      availableQtyForCartLine,
      payloadStockLevel,
    ])
  }

  const getCartTotalItemsCount = (cart = {}) => (
    _.sumBy(
      _.filter(
        _.get(cart, 'cartLineProperties', []),
        'isMainProperty',
      ),
      'quantity',
    )
  )

  const getCartValidationErrors = (validationErrors, options = {}) => {
    if (_.isEmpty(options)) return _.keys(validationErrors)
    const { type, ...filters } = options

    // Filter errors by type
    if (!_.isEmpty(type)) {
      switch (type) {
        case 'cart':
          return _.keys(
            _.pick(validationErrors, [
              'userNotEnoughLoyaltyPoints',
              'userCannotUseLoyaltyPoints',
            ]),
          )
        case 'skus':
          return _.keys(_.pick(validationErrors, ['inactiveSku', 'skuRequireCouponNotEnoughCoupon']))
        case 'skuQuota':
          return _.keys(
            _.pick(validationErrors, [
              'invalidSkuQuotaProfile',
              'invalidSkuQuota',
              'exceedSkuQuotaQuotaPerPerson',
              'notSupportedSkuQuota',
              'skuQuotaNotAvailable',
              'skuQuotaNotEnoughQuota',
              'skuQuotaInvalidConsumePeriod',
              'skuQuotaExceedCoupon',
              'skuQuotaCouponNotApplied',
            ]),
          )
        case 'shipment':
          return _.keys(
            _.pick(validationErrors, [
              'cartPriceTotalInsufficientForCourierService',
              'failedToCalculateShippingFee',
            ]),
          )
        case 'courierService':
          return _.keys(_.pick(validationErrors, ['cartPriceTotalInsufficientForCourierService']))
        case 'cartCouponToken':
          return _.keys(
            _.pick(validationErrors, [
              'cartCouponTokenHasNoApplicableSku',
              'notAppliedVoucherCartCouponToken',
              'notOwnedSelfUseOnlyCartCouponToken',
            ]),
          )
        case 'couponToken':
          return _.keys(
            _.pick(validationErrors, [
              'invalidCouponToken',
              'cartCouponTokenHasNoApplicableSku',
              'notAppliedVoucherCartCouponToken',
              'couponTokenNonusableOnCommerceChannelCouponToken',
              'notOwnedSelfUseOnlyCartCouponToken',
              'missingManualTriggerCouponToken',
              'reachApplyLimitPerOrderCouponToken',
              'reachApplyLimitPerCartDiscountCouponToken',
            ]),
          )
        default:
          return _.keys(validationErrors)
      }
    }

    // Filter by ids
    return _.keys(
      _.pickBy(validationErrors, (error) => {
        const {
          cartCouponTokenIds,
          cartLinePropertyIdentifierUuids,
          cartLinePropertyIds,
          cartManualCartDiscountIds,
          cartShipmentId,
          cartShipmentIds,
          couponIds,
          couponTokenIds,
          courierServiceId,
          skuIds,
          skuQuotaIds,
        } = _.get(error, 'data', {})
        const filterSize = _.size(_.keys(filters))
        let filterMatchCount = 0
        _.each(filters, (v, k) => {
          switch (k) {
            case 'cartCouponTokenId':
              filterMatchCount += _.includes(cartCouponTokenIds, v) ? 1 : 0
              break
            case 'cartLinePropertyId':
              filterMatchCount += _.includes(cartLinePropertyIds, v) ? 1 : 0
              break
            case 'cartLinePropertyIdentifierUuid':
              filterMatchCount += _.includes(cartLinePropertyIdentifierUuids, v) ? 1 : 0
              break
            case 'cartManualCartDiscountId':
              filterMatchCount += _.includes(cartManualCartDiscountIds, v) ? 1 : 0
              break
            case 'cartShipmentId':
              filterMatchCount
                += _.includes(cartShipmentIds, v) || _.isEqual(cartShipmentId, v) ? 1 : 0
              break
            case 'couponId':
              filterMatchCount += _.includes(couponIds, v) ? 1 : 0
              break
            case 'couponTokenId':
              filterMatchCount += _.includes(couponTokenIds, v) ? 1 : 0
              break
            case 'skuId':
              filterMatchCount += _.includes(skuIds, v) ? 1 : 0
              break
            case 'skuQuotaId':
              filterMatchCount += _.includes(skuQuotaIds, v) ? 1 : 0
              break
            case 'courierServiceId':
              filterMatchCount += _.isEqual(courierServiceId, v) ? 1 : 0
              break
            default:
              break
          }
        })
        return filterSize === filterMatchCount
      }),
    )
  }

  const getCartLineErrors = ({
    cart,
    cartLine,
    exclusions,
    storeMenuCodes,
  }) => {
    let inlineError = null
    const skuTotalQuantityInCart = getSkuTotalQuantityInCart({
      cart,
      skuId: _.get(cartLine, 'sku.id'),
    })
    if (skuTotalQuantityInCart > parseStockLevel(_.get(cartLine, 'sku.stockLevel'))) {
      inlineError = {
        message: t('screens.cart.errors.cartLine', {
          context: 'skuStockLevelExceeded',
        }),
        code: 'skuStockLevelExceeded',
      }
    }
    const skuValidationErrors = _.keys(_.omitBy(validateSku({
      sku: _.get(cartLine, 'sku'),
      storeMenuCodes,
      exclusions,
      storeCode: _.get(cart, 'cartShipments.0.inventoryStore.code'),
    })))
    if (!_.isEmpty(skuValidationErrors)) {
      inlineError = _.map(skuValidationErrors, (code) => (
        {
          message: t(
            'screens.cart.errors.cartLine',
            { context: code },
          ),
          code,
        }
      ))
    }
    const cartValidationErrors = _.get(cart, 'validationErrors', {})
    const validationErrors = _.uniq(
      _.compact(
        _.concat(
          // CartService.validateCartMax({ cart }) === true
          //   ? []
          //   : CartService.validateCartMax({ cart }),
          // getCartValidationErrors(
          //   cartValidationErrors,
          //   { type: 'cart' },
          // ),
          getCartValidationErrors(
            cartValidationErrors,
            { skuId: _.get(cartLine, 'sku.id') },
          ),
          getCartValidationErrors(
            cartValidationErrors,
            { cartLinePropertyId: _.get(cartLine, 'id') },
          ),
          getCartValidationErrors(
            cartValidationErrors,
            { skuQuotaId: _.get(cartLine, 'skuQuota.id') },
          ),
        ),
      ),
    )
    if (!_.isEmpty(validationErrors)) {
      inlineError = _.map(validationErrors, (code) => (
        {
          message: t(
            'screens.cart.errors.cartLine',
            { context: code },
          ),
          code,
        }
      ))
    }
    return _.compact(_.castArray(inlineError))
  }

  const cartsTotalItemsCount = _.reduce(
    cartIds,
    (result, id, commerceChannel) => ({
      ...result,
      [commerceChannel]: getCartTotalItemsCount(_.find(carts, { id })),
    }),
    {},
  )

  const totalItemsCount = _.sum([
    // cart for selected order method
    _.get(cartsTotalItemsCount, _.get(orderMethod, 'commerceChannel')),
    // cart for skuQuota
    _.get(cartsTotalItemsCount, _.get(env, 'config.api.commerceChannelForSkuQuota')),
  ])

  const resolveParams = (options) => {
    if (_.has(options, 'params')) {
      return {
        ...options,
        params: getParams(options.params),
      }
    }
    return getParams(options)
  }

  const handleUpdateCart = (options) => {
    const commerceChannel = getCommerceChannel(options)
    const currentCartId = _.get(cartIds, commerceChannel)
    return updateCart(resolveParams({
      cartId: currentCartId,
      ...options,
    }))
      .then((data) => {
        const updatedCart = _.get(data, 'cart')
        setCarts((prevCarts) => ([
          ..._.reject(prevCarts, { id: _.get(updatedCart, 'id') }),
          updatedCart,
        ]))
        return data
      })
  }

  const handleCreateCart = (options) => (
    createCart(resolveParams(options))
      .then((data) => {
        const cart = _.get(data, 'cart')
        const cartId = _.get(cart, 'id')
        setCarts((prevCarts) => ([
          ..._.reject(prevCarts, { id: cartId }),
          cart,
        ]))
        return data
      })
  )

  const handleFetchCart = (options) => (
    fetchCart(resolveParams(options))
      .then((data) => {
        const cart = _.get(data, 'cart')
        const cartId = _.get(cart, 'id')
        setCarts((prevCarts) => ([
          ..._.reject(prevCarts, { id: cartId }),
          cart,
        ]))
        return data
      })
  )

  const handleCreateIsolateCart = (options) => (
    createIsolateCart(resolveParams(options))
  )
  const handleFetchIsolateCart = (options) => (
    fetchIsolateCart(resolveParams(options))
  )
  const handleUpdateIsolateCart = (options) => (
    updateIsolateCart(resolveParams(options))
  )

  const handleMergeCart = (options) => {
    const { cartId, ...params } = options
    const payload = {
      data: {
        cart: {
          merging_cart_id: cartId,
        },
      },
    }
    return handleCreateCart({ payload, ...params })
  }

  const handleResetCart = () => {
    setCarts([])
    reset()
  }

  async function findOrCreateCart({
    cartId: cartIdOverride,
    // clean = false,
    orderMethodMandatory = true,
    // reduxUpdateCartAction = updateCartAction,
    ...params
  } = {}) {
    const commerceChannel = getCommerceChannel({
      orderMethodMandatory,
      ...params,
    })
    const currentCartId = _.get(cartIds, commerceChannel)
    const cartId = cartIdOverride || currentCartId
    const resolvedParams = resolveParams({
      cartId,
      commerceChannel,
      orderMethodMandatory,
      ...params,
    })
    // const cart = _.find(carts, { id: cartId })
    let data = {}
    try {
      // if cart.id is empty then throw error to create new cart
      if (!cartId) {
        throw {
          generalError: { code: 404 },
        }
      }
      data = await fetchCart({
        cartId,
        commerceChannel,
        ...resolvedParams,
      })
    } catch (errors) {
      // If the cart is no longer valid.
      // Re-create from the redux store.
      const isDineIn = (
        orderMethodMandatory
        && _.get(orderMethod, 'commerceChannel') === commerceChannel
        && _.get(orderMethod, 'commerceType') === 'dineIn'
      )

      if (
        isCartInvalid(errors)
        // prevent create cart when commerceChannel is for dine-in
        && !isDineIn
        && !_.includes(commerceChannelCodesForDineIn, commerceChannel)
      ) {
        let updateCartActions = []
        if (
          orderMethodMandatory
          && _.isEqual(commerceChannel, _.get(orderMethod, 'commerceChannel'))
        ) {
          // update cart shipment for current order method
          updateCartActions = _.reject(
            [
              // [TODO] insert cartShipmentId to each action
              getOrderMethodUpdateCartAction({
                orderMethod,
                deliveryAddressId: _.get(deliveryAddress, 'id'),
                pickupStoreId: _.get(store, 'id'),
              }),
            ],
            _.isEmpty,
          )
        }
        if (cartId) {
          data = await handleMergeCart({
            cartId,
            commerceChannel,
            params: resolveParams(params),
            updateCartActions,
          })
        } else {
          // const currentCart = _.find(carts, ['id', currentCartId])
          // const cartLines = _.get(currentCart, 'cartLineProperties', [])
          // if (!_.isEmpty(cartLines)) {
          //   const cartLineActions = _.map(cartLines, cartLine => (
          //     _.omitBy(
          //       {
          //         actionType: 'update_cart_line_property',
          //         skuId: _.get(cartLine, 'sku.id'),
          //         quantity: _.toNumber(cartLine.quantity),
          //         quantityMode: 'fixed',
          //         skuQuotaProfileId: _.get(cartLine, 'sku.skuQuotaProfileId'),
          //         skuQuotaId: _.get(cartLine, 'skuQuota.id'),
          //         productAddonId: _.get(cartLine, 'productAddon.id', null),
          //         ..._.pick(cartLine, [
          //           'id',
          //           'identifierUuid',
          //           'parentIdentifierUuid',
          //           'groupUuid',
          //           'customerRemark',
          //           'placeOrderSequence',
          //           'skuMeasure',
          //           'skuMeasureId',
          //           'skuQuotaWaitingList',
          //         ]),
          //       },
          //       _.isUndefined,
          //     )
          //   ))
          //   updateCartActions = [
          //     ...updateCartActions,
          //     ...cartLineActions,
          //   ]
          // }
          data = await handleCreateCart({
            commerceChannel,
            params: resolveParams(params),
            updateCartActions,
          })
        }
      } else if (
        isDineIn
        && isCartCompleted(errors)
      ) {
        dineInCompleted()
        throw errors
      } else {
        throw errors
      }
    }
    const newCart = _.get(data, 'cart')
    const newCartId = _.get(newCart, 'id')
    setCarts((prevCarts) => ([
      ..._.reject(prevCarts, ({ id }) => _.includes([cartId, newCartId], id)),
      newCart,
    ]))
    return data
  }

  const getDineInStagingCart = () => {
    try {
      return localStorage.get('dineInStagingCart')
    } catch (error) {
      console.log('//// Error: getDineInStagingCart ////', error)
      return { actions: [], totalItems: 0 }
    }
  }

  const mergeCartActions = (previousActions, currentActions) => {
    const actionsToRemove = _.filter(currentActions, ({ quantity }) => quantity === 0)
    const actionsToAdd = _.filter(currentActions, ({ quantity }) => quantity > 0)
    const uuidsToRemove = _.keys(_.groupBy(actionsToRemove, 'groupUuid'))
    const otherActions = _.reject(
      previousActions,
      ({ groupUuid, identifierUuid }) => (
        _.includes(uuidsToRemove, groupUuid)
        || _.includes(_.map(currentActions, 'identifierUuid'), identifierUuid)
      ),
    )
    return [
      ...otherActions,
      ...actionsToAdd,
    ]
  }

  const clearDineInStagingCart = () => {
    setDineInStagingCart({ actions: [], totalItems: 0, cartId: null })
  }

  const updateDineInStagingCart = ({ actions = [] }) => {
    setDineInStagingCart((prevStagingCart) => {
      const updatedActions = mergeCartActions(
        _.get(prevStagingCart, 'actions', []),
        actions,
      )
      const totalItems = _.sumBy(
        _.filter(
          updatedActions,
          (action) => _.isNull(_.get(action, 'productAddonId', null)),
        ),
        'quantity',
      )
      return { actions: updatedActions, totalItems, cartId: dineInCartId }
    })
  }

  const dineInCompleted = () => {
    // prevent navigate when open table in progress
    const path = _.get(location, 'pathname')
    if (!_.includes(path, 'dine-in')) {
      navigate('/dine-in/', { replace: true, commerceChannel: null })
    }
  }

  // Return signle cart id by getCommerceChannel function
  const getCartId = useCallback((...args) => {
    const commerceChannel = getCommerceChannel(...args)
    return _.get(cartIds, commerceChannel)
  }, [cartIds])
  // Return signle cart by getCommerceChannel function
  const getCart = useCallback((...args) => {
    const cartId = getCartId(...args)
    return _.find(carts, { id: cartId })
  }, [getCartId, carts])

  const state = {
    cartIds,
    carts,
    cartsTotalItemsCount,
    cc,
    checkout,
    clearDineInStagingCart,
    updateDineInStagingCart,
    getCartLineAvailableQuantity,
    getCartLineErrors,
    getCartTotalItemsCount,
    getCartValidationErrors,
    dineInStagingCart,
    getCart,
    getCartId,
    getParams,
    getSkuTotalQuantityInCart,
    findOrCreateCart,
    fetchCart: handleFetchCart,
    fetchIsolateCart: handleFetchIsolateCart,
    updateCart: handleUpdateCart,
    updateIsolateCart: handleUpdateIsolateCart,
    createCart: handleCreateCart,
    createIsolateCart: handleCreateIsolateCart,
    mergeCart: handleMergeCart,
    resetCart: handleResetCart,
    refreshCarts,
    refreshCartsInProgress,
    totalItemsCount,
    isCartCompleted,
    isCartFreezed,
    initRefreshCartsReady,
  };

  return (
    <CartContext.Provider value={state}>
      {children}
    </CartContext.Provider>
  )
}
